<template>
  <tr v-for="refinement,index in group" :key="index" :class="{'booth-refinement__group': isSeamlessPrint}">
    <td>
      {{ (inEnglish) ? refinement.booth_refinement.name_en : refinement.booth_refinement.name }}
      <span v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED"
        class="cursor--pointer text-primary"
        @click="showEditRefinementModal(refinement)">
        <i class="fa fa-pencil ml-1"></i>
      </span>
    </td>
    <template v-if="index === 0">
      <td :rowspan="group.length">
        <span class="text-primary cursor--pointer" @click="show">
          <i class="fa fa-info-circle"></i> {{ articleName }} <span v-if="isSeamlessPrint">({{ $t('configurator.form.labels.seamless_print') }})</span>
        </span>

        <span v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED"
          class="cursor--pointer text-primary"
          @click="showEditPrintArticleModal">
          <i class="fa fa-pencil ml-1"></i>
        </span>

      </td>
    </template>
    
    <td class="text-right">
      {{ $filters.formatMoney(refinement.exhibition_prices.price_net) }}
    </td>
    
    <template v-if="index === 0">
      <td v-if="cloud.status < PRINT_CLOUD_STATUS_FINISHED" :rowspan="group.length">
        <span v-if="configuration.is_logo_print">
          <span v-if="configuration.upload && configuration.upload.file_exists"
            class="text-primary cursor--pointer"
            @click="download">
            <i class="fa fa-download"></i> {{ configuration.upload.original_name }}
          </span>
          <span v-if="configuration.upload && !configuration.upload.file_exists">
            <span class="text-primary">{{ configuration.upload.original_name }}</span><br>
            <span class="mr-2 bg-danger">
              <i class="fa fa-warning text-danger"></i> {{ $t('configurator.labels.file_missing_reupload') }}
            </span>
            <span v-if="isCloudStatusOpen"
              class="text-primary cursor--pointer"
              @click="showUploadModal">
              <i class="fa fa-upload mr-1"></i> {{ $t('configurator.buttons.upload_now') }}
            </span>
          </span>
          <span v-if="!configuration.upload">
            <span class="bg-warning">
              <i class="fa fa-warning text-warning"></i> {{ $t('configurator.labels.upload_missing') }}
            </span>
            
            <span v-if="isCloudStatusOpen"
              class="text-primary cursor--pointer"
              @click="showUploadModal">
              <i class="fa fa-upload mr-1"></i> {{ $t('configurator.buttons.upload_now') }}
            </span>
          </span>
        </span>
        <span v-else>
          {{ configuration.label }}
        </span>
      </td>
      <td v-else :rowspan="group.length">
        <span v-if="configuration.is_logo_print">
          <span v-if="configuration.upload" class="text-primary">
            {{ configuration.upload.original_name }} ({{ $t('configurator.labels.deleted') }})
          </span>
        </span>
        <span v-else>
          {{ configuration.label }}
        </span>
      </td>
      <td :rowspan="group.length">
        <span v-if="configuration.upload && isCloudStatusOpen"
          class="text-primary cursor--pointer"
          @click="confirmDeleteUpload">
          <i class="fa fa-trash"></i> {{ $t('configurator.buttons.delete_graphic_file') }}
        </span>

        <span v-if="!configuration.is_logo_print && isCloudStatusOpen"
          class="text-primary cursor--pointer"
          @click="showEditModal">
          <i class="fa fa-pencil mr-1"></i> {{ $t('configurator.buttons.edit_lettering') }}
        </span>
      </td>
    </template>
  </tr>
  <modal :is-active="uploadModalActive" :title="$t('configurator.modals.titles.upload_graphic_file')">
    <template #body>
      <div class="form-group">
        <label for="property-images">{{ $t('configurator.form.labels.select_file') }}:</label>
        <drop-zone :id="dropzoneId" @drop.prevent="drop" @change="selectedFile"></drop-zone>
        <p class="help-block mb-1 pb-1" style="font-weight: 400;">
          {{ $t('configurator.form.labels.selected_file') }}: <span v-if="dropzoneFile">{{ dropzoneFile.name }}</span>
        </p>
        <printing-information></printing-information>
      </div>
      <div class="form-group">
        <div class="row">
          <div class="col-md-12 text-center">
            <button class="btn btn-warning mr-2" @click="cancelUpload">
              {{ $t('configurator.buttons.cancel') }}
            </button>
            <button class="btn btn-primary" @click="upload">
              {{ $t('configurator.buttons.upload_graphic_file') }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </modal>
  <EditBoothRefinementLabelModal v-if="editLabelModalActive"
    :refinement="group[0]"
    :form="editLabelForm"
    @update="updateLabel"
    @cancel="hideEditModal"/>

  <EditBoothRefinementModal v-if="selectedRefinement"
    :is-active="editRefinementModalActive"
    :refinement="selectedRefinement"
    :is-seamless-print="isSeamlessPrint"
    :is-group="true"
    @hide="hideEditRefinementModal" />

  <EditBoothRefinementPrintArticleModal v-if="editPrintArticleModalActive"
    :refinement="group[0]"
    :is-seamless-print="isSeamlessPrint"
    :is-group="true"
    @hide="hideEditPrintArticleModal" />
</template>

<script setup>
import { computed, ref, reactive } from 'vue'
import { useMainStore } from '@/store'
import { useCloudStore } from '@/stores/cloud'
import { PRINT_CLOUD_STATUS_FINISHED, PRINT_CLOUD_STATUS_OPEN } from '@/constants'
import Form from '@/classes/Form'
import FormDataForm from '@/classes/FormDataForm'
import Swal from 'sweetalert2'
import axios from 'axios'
import { showErrorToast, showLoadingIndicator, showSuccessToast, showWarningToast } from '@/utils/toast'
import { useI18n } from 'vue-i18n'
import EditBoothRefinementLabelModal from '@/components_new/PrintCloud/EditBoothRefinementLabelModal.vue'
import EditBoothRefinementPrintArticleModal from '@/components_new/PrintCloud/EditBoothRefinementPrintArticleModal.vue'
import EditBoothRefinementModal from '@/components_new/PrintCloud/EditBoothRefinementModal.vue'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
const cloudStore = useCloudStore()
// define props
const props = defineProps({
  group: {
    type: Array,
    required: true
  }
})
// define emits
const emit = defineEmits(['download', 'show'])
// define reactive props
const dropzoneFile = ref('')
const uploadModalActive = ref(false)
const uploadForm = reactive(new FormDataForm({
  file: null,
  configuration_id: null,
  use_graphic_for_all_prints: false,
}))
const editLabelModalActive = ref(false)
const editLabelForm = reactive(new Form({
  label: ''
}))
const editPrintArticleModalActive = ref(false)
const editRefinementModalActive = ref(false)
const selectedRefinement = ref(null)
// define computed props
const locale = computed(() => store.settings.locale)
const cloud = computed(() => store.tmp.cloud)

const isCloudStatusOpen = computed(() => {
  return cloud.value.status === PRINT_CLOUD_STATUS_OPEN
})

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const articleName = computed(() => {
  return (inEnglish.value)
    ? props.group[0].article.name_en
    : props.group[0].article.name
})

const isSeamlessPrint = computed(() => {
  return configuration.value.is_seamless_print
})

const configuration = computed(() => {
  return props.group[0].configurations[0]
})

const dropzoneId = computed(() => {
  return 'dropzone_booth_' + configuration.value.id
})
// define methods
const show = () => {
  emit('show', props.group[0].article)
}

const download = () => {
  emit('download', configuration.value.upload)
}

const confirmDeleteUpload = () => {
  Swal.fire({
    title: t('configurator.notices.titles.delete_upload'),
    text: t('configurator.notices.messages.delete_upload'),
    showCancelButton: true,
    confirmButtonText: t('configurator.buttons.yes'),
    cancelButtonText: t('configurator.buttons.no'),
  })
    .then((result) => {
      if (result.isConfirmed) {
        deleteUpload()
      }
    })
}

const deleteUpload = async () => {
  showLoadingIndicator(t('print_cloud.indicator.delete_upload'))
  await axios.delete('/print/' + cloud.value.uuid + '/upload/' + configuration.value.upload.id)
    .then(response => {
      cloudStore.data.print_articles = response.data.articles
      store.tmp.booth = response.data.booth
      showSuccessToast(t('configurator.toasts.messages.upload_deleted'), t('toast.title.success'))
    })
    .catch(err => {
      console.error(err)
      showErrorToast(t('toast.message.error'), t('toast.title.error'))
    })
}

const showUploadModal = (slot) => {
  uploadForm.slot = slot
  uploadModalActive.value = true
}

const hideUploadModal = () => {
  uploadModalActive.value = false
}

const drop = (e) => {
  dropzoneFile.value = e.dataTransfer.files[0]
}

const selectedFile = () => {
  dropzoneFile.value = document.getElementById(dropzoneId.value).files[0]
}

const upload = async () => {
  showLoadingIndicator(t('configurator.indicators.upload'))
  uploadForm.file = dropzoneFile.value
  uploadForm.configuration_id = configuration.value.id
  await uploadForm.post('/print/' + cloud.value.uuid + '/upload')
    .then((response) => {
      cloudStore.data.print_articles = response.data.articles
      store.tmp.booth = response.data.booth
      uploadForm.resetToOriginal()
      dropzoneFile.value = ''
      hideUploadModal()
      showSuccessToast(t('configurator.toasts.messages.upload_successful'), t('toast.title.success'))
    })
    .catch(err => {
      if (err.status === 413) {
        showWarningToast(t('toast.warnings.too_large'), t('toast.title.warning'), 5000)
      } else {
        showErrorToast(t('toast.message.error'), t('toast.title.error'))
      }
    })
}

const cancelUpload = async () => {
  dropzoneFile.value = ''
  hideUploadModal()
}

const showEditModal = () => {
  editLabelForm.label = configuration.value.label ?? ''
  editLabelModalActive.value = true
}

const hideEditModal = () => {
  editLabelModalActive.value = false
}

const updateLabel = async () => {
  showLoadingIndicator(t('configurator.indicators.update_lettering'), t('configurator.indicators.one_moment_please'))
  await cloudStore.updateConfigurationLabel(editLabelForm, configuration.value.id)
    .then(data => {
      showSuccessToast(t('configurator.toasts.messages.lettering_updated'), t('toast.title.success'))
      hideEditModal()
    })
    .catch(_err => {
      showErrorToast(t('configurator.toasts.errors.lettering_updated'), t('toast.title.error'))
    })
}

const showEditRefinementModal = (refinement) => {
  selectedRefinement.value = refinement
  editRefinementModalActive.value = true
}

const hideEditRefinementModal = () => {
  editRefinementModalActive.value = false
}

const showEditPrintArticleModal = () => {
  editPrintArticleModalActive.value = true
}

const hideEditPrintArticleModal = () => {
  editPrintArticleModalActive.value = false
}
</script>